import { Button, Drawer, Row, Space, message } from "antd";
import styles from "./index.less";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  AskAIType,
  GeneratingStatus,
  StreamResponseDataType,
  streamFetch,
} from "../FlowEditPage/utils/streamFetch";
import { nanoid } from "nanoid";
import { produce } from "immer";
import DetailModal from "./DetailModal";
import { ReactComponent as ClearIcon } from "../assets/clear.svg";
import ChatItemHuman from "./ChatItems/ChatItemHuman";
import ChatItemAI from "./ChatItems/ChatItemAI";
import ChatInput from "./ChatInput";
import ChatPluginMockInput from "./ChatPluginMockInput";
import { clearChatHistoryService } from "../FlowEditPage/services/app";
import { ResponseCode } from "../constants";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import {
  CardMessage,
  ChatMessageType,
  FileMessage,
  ImageMessage,
  TextMessage,
  VideoMessage,
  VoiceMessage,
} from "./types";
import {
  queryWelcomeWordsService,
  clearTemplateHistoryService,
  getTemplateFlowService,
} from "../FlowEditPage/services/template";
import {
  clearPluginHistoryService,
  getPluginFlowService,
} from "../FlowEditPage/services/plugin";
import ChatItemPluginInput from "./ChatItems/ChatItemPluginInput";
import { FlowNodeInputItemType } from "@packages/flow-core/module/node/types";
import ChatItemTemplateInput from "./ChatItems/ChatTemplateInput";
import classNames from "classnames";

type Role = "human" | "ai" | "plugin_input" | "template_input";

type ChatDrawerProps = {
  visible: boolean;
  onClose: () => void;
  _id: string;
  mode: "app" | "plugin" | "template";
  interruptOldOnNew?: boolean;
  className?: string;
};

type ChatHistory = {
  msg?: ChatMessageType;
  status: GeneratingStatus;
  role: Role;
  streamResponseData?: StreamResponseDataType[];
  id: string;
};

export default function ChatDrawer({
  visible,
  onClose,
  _id,
  mode,
  interruptOldOnNew = true,
  className,
}: ChatDrawerProps) {
  const [chatId, setChatId] = useState("");
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [responseData, setResponseData] = useState<StreamResponseDataType[]>();

  const [history, setHistory] = useState<ChatHistory[]>([]);
  const [pluginInputs, setPluginInputs] = useState<FlowNodeInputItemType[]>();
  const [templateInputs, setTemplateInputs] =
    useState<FlowNodeInputItemType[]>();
  const [templateParam, setTemplateParam] = useState<Record<string, any>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const pluginIdRef = useRef<string>();

  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [abortSignals, setAbortSignals] = useState<[string, AbortController][]>(
    []
  );

  const [disableToSubmitPluginInputIds, setDisableToSubmitPluginInputIds] =
    useState<string[]>([]);

  // 需要去打断对话
  const needToInterrupt =
    (mode === "app" && interruptOldOnNew) ||
    mode === "plugin" ||
    mode === "template";

  useEffect(() => {
    if (mode !== "plugin" || !_id) {
      setPluginInputs([]);
      return;
    }

    // TODO 暂时不支持插件
    getPluginFlowService(_id).then((rsp) => {
      if (rsp.code === ResponseCode.Success) {
        setPluginInputs(
          rsp?.data?.modules?.find(
            (m) => m.flowType === FlowNodeTypeEnum.pluginInput
          )?.inputs ?? []
        );
        return;
      }

      return [];
    });
  }, [visible, mode, _id]);
  useEffect(() => {
    if (mode !== "template" || !_id) {
      setTemplateInputs([]);
      return;
    }

    getTemplateFlowService(_id).then((rsp) => {
      if (rsp.code === ResponseCode.Success) {
        setTemplateInputs(
          rsp?.data?.modules?.find(
            (m) => m.flowType === FlowNodeTypeEnum.templateParam
          )?.inputs ?? []
        );
        return;
      }

      return [];
    });
  }, [visible, mode, _id]);

  const fetchUrl = useMemo(() => {
    switch (mode) {
      case "app":
        return `/api/insnail-flow-platform/flow/runtime/test/${_id}`;
      case "plugin": // 暂不支持插件
        return `/api/insnail-flow-platform/damselfish/plugin/runtime/test/${_id}`;
      case "template":
        return `/api/insnail-flow-platform/damselfish/flowTemplate/runtime/test/${_id}`;
    }
  }, [mode, _id]);

  const getWelcomeWords = (flowId: string, chatId: string) => {
    queryWelcomeWordsService(flowId, chatId).then((rsp) => {
      console.log("rsp", rsp);
      if (rsp.code !== 200) {
        message.error(rsp.msg);
        return;
      }
      rsp.data.forEach((item) => {
        handleMessage(item);
      });
    });
  };

  const removeLoadingStatusById = (updateId: string) => {
    setLoadingIds((state) => state.filter((id) => id !== updateId));
    setAbortSignals((state) => state.filter(([id]) => id !== updateId));
  };

  const handleMessage = (data: StreamResponseDataType) => {
    // 文本输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.messageOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as TextMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { ...outputMap, type: "text" },
            id: nanoid(),
          });
        })
      );
    }
    // 文件输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.fileOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as FileMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { type: "file", name: outputMap.name, url: outputMap.url },
            id: nanoid(),
          });
        })
      );
    }
    // 视频输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.videoOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as VideoMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { type: "video", name: outputMap.name, url: outputMap.url },
            id: nanoid(),
          });
        })
      );
    }
    // 图片输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.imageOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as ImageMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { ...outputMap, type: "image" },
            id: nanoid(),
          });
        })
      );
    }
    // 卡片输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.cardOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as CardMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: {
              ...outputMap,
              type: "card",
            },
            id: nanoid(),
          });
        })
      );
    }

    // 语音输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.voiceOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as VoiceMessage;
      setHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: {
              ...outputMap,
              type: "voice",
            },
            id: nanoid(),
          });
        })
      );
    }
  };

  // 用户输入问题方法，插件直接 JSON.stringify 进行复用
  async function handleAskAI(
    question: string,
    type: AskAIType,
    extraInfo?: any
  ) {
    // 更改打断逻辑
    if (needToInterrupt && loadingIds.length > 0) {
      handleStop();
    }

    // 当前会话id
    let updateId = "";

    if (mode === "app" || mode === "template") {
      updateId = nanoid();
      setLoadingIds(
        produce((state) => {
          state.push(updateId);
        })
      );
    }

    if (mode === "plugin") {
      updateId = pluginIdRef.current!;
    }

    // AI机器人限定历史记录，插件输入直接先扔一条数据进去了
    if (mode === "app" || mode === "template") {
      if (type === "TEXT") {
        setHistory(
          produce((state) => {
            state.push({
              role: "human",
              status: "running",
              msg: {
                // @ts-ignore 类型大小写的区别
                type: type.toLowerCase(),
                messageOutput: question,
              },
              id: updateId,
            });
          })
        );
      }

      if (type === "FILE") {
        setHistory(
          produce((state) => {
            state.push({
              role: "human",
              status: "running",
              msg: {
                // @ts-ignore 类型大小写的区别
                type: "file",
                url: question,
                name: extraInfo.name,
              },
              id: updateId,
            });
          })
        );
      }

      // 这几种类型定义不一致
      if (["IMAGE", "VOICE", "VIDEO"].includes(type)) {
        setHistory(
          produce((state) => {
            state.push({
              role: "human",
              status: "running",
              msg: {
                // @ts-ignore 类型大小写的区别
                type: type.toLowerCase(),
                url: question,
              },
              id: updateId,
            });
          })
        );
      }

      setValue("");
    }

    const abortSignal = new AbortController();
    setAbortSignals(
      produce((state) => {
        state.push([updateId, abortSignal]);
      })
    );
    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 300);

    try {
      const { responseData } = await streamFetch({
        url: fetchUrl,
        data:
          mode === "plugin"
            ? { userChatInput: question, chatId: chatId }
            : {
                userChatInput: question,
                chatId: chatId,
                messageType: type,
                templateParam,
              },
        onMessage(data) {
          setTimeout(() => {
            containerRef.current?.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }, 100);
          if (data.status === "done") {
            removeLoadingStatusById(updateId);
            setHistory(
              produce((state) => {
                const item = state.find((item) => item.id === updateId);
                if (item) {
                  item.status = "done";
                }
              })
            );
          }
          if (!!data?.dialog) {
            handleMessage(data);
          }
        },
        onClose() {
          removeLoadingStatusById(updateId);
          setHistory(
            produce((state) => {
              const item = state.find((item) => item.id === updateId);
              if (item) {
                item.status = "done";
              }
            })
          );
        },
        abortCtrl: abortSignal,
      });

      removeLoadingStatusById(updateId);
      setHistory(
        produce((state) => {
          const item = state.find((item) => item.id === updateId);
          if (item) {
            item.status = "done";
            item.streamResponseData = responseData;
          }
        })
      );
    } catch (err) {
      message.error((err as Error).message);
      setHistory(
        produce((state) => {
          const item = state.find((item) => item.id === updateId);
          if (item) {
            item.status = "done";
          }
        })
      );
    }
  }

  function handleStop() {
    if (loadingIds.length > 0) {
      setHistory(
        produce((state) => {
          for (let i = 0; i < state.length; i++) {
            const item = state[i];
            if (item && loadingIds.includes(item.id)) {
              item.status = "done";
            }
          }
        })
      );
      abortSignals.forEach((s) => s[1].abort());
      setLoadingIds([]);
      setAbortSignals([]);
    }
  }

  const appendNewPluginInput = () => {
    if (mode === "plugin") {
      handleStop();

      const pluginInputId = nanoid();
      setLoadingIds(
        produce((state) => {
          state.push(pluginInputId);
        })
      );

      setHistory(
        produce((state) => {
          state.push({
            role: "plugin_input",
            status: "running",
            id: pluginInputId,
          });
        })
      );
      pluginIdRef.current = pluginInputId;
    }
  };

  const title = useMemo(() => {
    switch (mode) {
      case "app":
        return "对话调试";
      case "plugin":
        return "子流程调试";
      case "template":
        return "模板调试";
    }
  }, [mode]);

  return (
    <Drawer
      mask={false}
      maskClosable={false}
      onClose={onClose}
      open={visible}
      closable
      width={500}
      destroyOnClose
      afterOpenChange={(open) => {
        if (!open) {
          handleStop();
          setHistory([]);
        } else {
          const newChatId = `${_id}-${nanoid()}`;
          setChatId(newChatId);
          if (mode === "app") {
            getWelcomeWords(_id, newChatId);
          }
        }
      }}
      title={
        <Row justify="space-between" align="middle">
          <span className={styles.title}>{title}</span>
          <Space>
            <Button
              type="text"
              icon={<ClearIcon />}
              onClick={async () => {
                if (loadingIds.length > 0) {
                  handleStop();
                }
                const api =
                  mode === "app"
                    ? clearChatHistoryService
                    : mode === "plugin"
                    ? clearPluginHistoryService
                    : clearTemplateHistoryService;
                const rsp = await api(chatId);
                if (rsp.code === ResponseCode.Success) {
                  message.success("清除成功");
                  setHistory([]);
                  setTemplateParam(undefined);
                } else {
                  message.error(rsp.msg);
                }
              }}
            />
          </Space>
        </Row>
      }
      rootClassName={classNames(styles.drawer, className)}
    >
      <div className={styles.main}>
        <div className={styles.content} ref={containerRef}>
          {mode === "template" && !!templateInputs?.length && (
            <ChatItemTemplateInput
              inputs={templateInputs!}
              onSubmit={(values: Record<string, any>) => {
                setTemplateParam(values);
              }}
              disableToSubmit={!!templateParam}
            />
          )}
          {history.map((item) => (
            <Fragment key={item.id}>
              {item.role === "human" && (
                <ChatItemHuman
                  msg={item.msg!}
                  streamResponseData={item.streamResponseData}
                  loading={item.status === "running"}
                  onRefresh={() => {
                    if (item.msg!.type === "text") {
                      handleAskAI(item.msg!.messageOutput, "TEXT");
                    }

                    if (item.msg!.type === "file") {
                      handleAskAI(
                        item.msg!.url,
                        item.msg!.type.toUpperCase() as AskAIType,
                        {
                          name: item.msg!.name,
                        }
                      );
                    }

                    if (["video", "image"].includes(item.msg!.type)) {
                      // @ts-ignore 看类型定义
                      handleAskAI(
                        // @ts-ignore 看类型定义
                        item.msg.url,
                        // @ts-ignore 看类型定义
                        item.msg.type.toUpperCase() as AskAIType
                      );
                    }
                  }}
                  onViewDetail={() => {
                    setOpen(true);
                    setResponseData(item.streamResponseData!);
                  }}
                  onDelete={() => {
                    //TODO: 删除
                  }}
                />
              )}

              {/* 渲染插件输入 */}
              {item.role === "plugin_input" && !!pluginInputs && (
                <ChatItemPluginInput
                  loading={item.status === "running"}
                  streamResponseData={item.streamResponseData}
                  inputs={pluginInputs}
                  disableToSubmit={disableToSubmitPluginInputIds.includes(
                    item.id
                  )}
                  onRunPlugin={(values: Record<string, unknown>) => {
                    setDisableToSubmitPluginInputIds(
                      produce((state) => {
                        state.push(pluginIdRef.current as string);
                      })
                    );
                    // 为了兼容，插件情况下并不支持多模态的类型定义
                    handleAskAI(JSON.stringify(values), "TEXT");
                  }}
                  onRefresh={(values: Record<string, unknown>) => {
                    handleAskAI(JSON.stringify(values), "TEXT");
                  }}
                  onViewDetail={() => {
                    setOpen(true);
                    setResponseData(item.streamResponseData!);
                  }}
                />
              )}

              {item.role === "ai" && (
                <ChatItemAI
                  msg={item.msg!}
                  onDelete={() => {
                    //TODO: 删除
                  }}
                />
              )}
            </Fragment>
          ))}
        </div>
        {/* 用户输入模式下问题数据 */}
        {mode === "app" && (
          <ChatInput
            value={value}
            onChange={(v) => setValue(v)}
            onEnter={handleAskAI}
          />
        )}

        {/* 插件模式下用户输入数据，新增一条历史记录进行插入 */}
        {mode === "plugin" && !!pluginInputs && (
          <ChatPluginMockInput
            disabled={loadingIds.length > 0}
            inputs={pluginInputs}
            onAppendNewPluginInput={appendNewPluginInput}
          />
        )}
        {/* 模板模式需要先填写表单才能输入问题 */}
        {mode === "template" && (templateParam || !templateInputs?.length) && (
          <ChatInput
            value={value}
            onChange={(v) => setValue(v)}
            onEnter={handleAskAI}
          />
        )}
      </div>
      {/* 过滤掉对话框输出的数据源 */}
      <DetailModal
        open={open}
        onClose={() => setOpen(false)}
        responseData={
          responseData?.filter(
            (data) => JSON.stringify(data) !== "{}" && !data?.dialog
          ) || []
        }
      />
    </Drawer>
  );
}
